/**
 * Access Request API service layer.
 * Calls GraphQL APIs for access request operations.
 */

import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { AccessRequestEntity, CorpSegmentEntry } from 'src/models/AccessRequestModel';
import * as queries from 'src/graphql/queries';
import * as mutations from 'src/graphql/mutations';

// Re-export model types for convenience
export type { AccessRequestEntity };

// ─── Types matching the GraphQL schema ───────────────────────────────────────

/** Alias for CorpSegmentEntry — used in API input types */
export type CorpSegment = CorpSegmentEntry;

export interface AccessRequestFilter {
  data_classification_ids?: number[];
  statuses?: string[];
  user_alias?: string;
  requester_alias?: string;
  start_date?: string;
  end_date?: string;
}

// ─── Submit ──────────────────────────────────────────────────────────────────

export interface CreateAccessRequestInput {
  data_classification_id: number;
  requested_role: string;
  corp_segments: CorpSegment[];
  justification: string;
  on_behalf_of?: { alias: string; name: string }[];
}

interface SubmitAccessRequestsResponse {
  submitAccessRequests: {
    results: { request_id: string; data_classification_id: number; business_group_name: string; user_alias: string; status: string; created_at: string }[];
    errors: { data_classification_id: number; user_alias: string; code: string; message: string }[];
  };
}

export const submitAccessRequests = async (requests: CreateAccessRequestInput[]): Promise<SubmitAccessRequestsResponse['submitAccessRequests']> => {
  const data = await fetchGraphQLRequest<SubmitAccessRequestsResponse>({
    query: mutations.submitAccessRequests,
    variables: { input: { requests } },
    requestName: 'submitAccessRequests',
    requestType: 'mutation'
  });
  return data.submitAccessRequests;
};

// ─── Cancel ──────────────────────────────────────────────────────────────────

interface CancelAccessRequestsResponse {
  cancelAccessRequests: {
    results: { request_id: string; status: string; updated_at: string }[];
    errors: { request_id: string; code: string; message: string }[];
  };
}

export const cancelAccessRequests = async (requestIds: string[]): Promise<CancelAccessRequestsResponse['cancelAccessRequests']> => {
  const data = await fetchGraphQLRequest<CancelAccessRequestsResponse>({
    query: mutations.cancelAccessRequests,
    variables: { input: { request_ids: requestIds } },
    requestName: 'cancelAccessRequests',
    requestType: 'mutation'
  });
  return data.cancelAccessRequests;
};

// ─── Approve ─────────────────────────────────────────────────────────────────

export interface ApproveAccessRequestItem {
  request_id: string;
  approved_corp_segments: CorpSegment[];
  approved_role?: string;
  approval_reason?: string;
}

interface ApproveAccessRequestsResponse {
  approveAccessRequests: {
    results: { request_id: string; status: string; updated_at: string }[];
    errors: { request_id: string; code: string; message: string }[];
  };
}

export const approveAccessRequests = async (requests: ApproveAccessRequestItem[]): Promise<ApproveAccessRequestsResponse['approveAccessRequests']> => {
  const data = await fetchGraphQLRequest<ApproveAccessRequestsResponse>({
    query: mutations.approveAccessRequests,
    variables: { input: { requests } },
    requestName: 'approveAccessRequests',
    requestType: 'mutation'
  });
  return data.approveAccessRequests;
};

// ─── Reject ──────────────────────────────────────────────────────────────────

interface RejectAccessRequestsResponse {
  rejectAccessRequests: {
    results: { request_id: string; status: string; updated_at: string }[];
    errors: { request_id: string; code: string; message: string }[];
  };
}

export const rejectAccessRequests = async (
  requests: { request_id: string; rejection_reason: string }[]
): Promise<RejectAccessRequestsResponse['rejectAccessRequests']> => {
  const data = await fetchGraphQLRequest<RejectAccessRequestsResponse>({
    query: mutations.rejectAccessRequests,
    variables: { input: { requests } },
    requestName: 'rejectAccessRequests',
    requestType: 'mutation'
  });
  return data.rejectAccessRequests;
};

// ─── List (My Requests — where I'm the beneficiary) ─────────────────────────

interface ListMyAccessRequestsResponse {
  listMyAccessRequests: AccessRequestEntity[];
}

export const listMyAccessRequests = async (): Promise<AccessRequestEntity[]> => {
  const data = await fetchGraphQLRequest<ListMyAccessRequestsResponse>({
    query: queries.listMyAccessRequests,
    variables: {},
    requestName: 'listMyAccessRequests',
    requestType: 'query'
  });
  return data.listMyAccessRequests ?? [];
};

// ─── List (My Submissions — where I'm the requester) ─────────────────────────

interface ListMySubmissionsResponse {
  listMyAccessRequestSubmissions: AccessRequestEntity[];
}

export const listMyAccessRequestSubmissions = async (): Promise<AccessRequestEntity[]> => {
  const data = await fetchGraphQLRequest<ListMySubmissionsResponse>({
    query: queries.listMyAccessRequestSubmissions,
    variables: {},
    requestName: 'listMyAccessRequestSubmissions',
    requestType: 'query'
  });
  return data.listMyAccessRequestSubmissions ?? [];
};

// ─── List (Admin — pending requests for business groups) ─────────────────────

interface ListAccessRequestsResponse {
  listAccessRequests: AccessRequestEntity[];
}

export const listAccessRequests = async (filter?: AccessRequestFilter): Promise<AccessRequestEntity[]> => {
  const data = await fetchGraphQLRequest<ListAccessRequestsResponse>({
    query: queries.listAccessRequests,
    variables: { filter: filter || {} },
    requestName: 'listAccessRequests',
    requestType: 'query'
  });
  return data.listAccessRequests ?? [];
};

// ─── Clone ───────────────────────────────────────────────────────────────────

interface CloneUserAccessResponse {
  cloneUserAccess: {
    access: { data_classification_id: number; business_group_name: string; user_type: string; corp_segment_access: string }[];
  };
}

export const cloneUserAccess = async (
  sourceAlias: string,
  targetAlias: string,
  targetName: string,
  dataClassificationIds?: number[]
): Promise<CloneUserAccessResponse['cloneUserAccess']> => {
  const data = await fetchGraphQLRequest<CloneUserAccessResponse>({
    query: mutations.cloneUserAccess,
    variables: {
      input: {
        source_alias: sourceAlias,
        target_alias: targetAlias,
        target_name: targetName,
        data_classification_ids: dataClassificationIds || null
      }
    },
    requestName: 'cloneUserAccess',
    requestType: 'mutation'
  });
  return data.cloneUserAccess;
};
